import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { FiMenu } from "react-icons/fi";
import styled from "styled-components";
import {
  links,
  discordUrl,
  twitterUrl,
  login_url_discord,
  redirect_login_url,
  api_v3_list_of_players,
  api_v3_register,
  randomAvatar,
} from "../utils/constants";
import { useNavContext } from "../context/nav_context";
import { MdErrorOutline } from "react-icons/md";
import { TfiSearch } from "react-icons/tfi";
import { Loading } from "../components";
import { ImageWithFallback } from "../components/match";
import Fuse from "fuse.js";

const Nav = () => {
  const inputRef = useRef(null);
  const resultListRef = useRef(null);

  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();

  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const [loadPlayersInProgress, setLoadPlayersInProgress] = useState(false);
  const [loadPlayersError, setLoadPlayersError] = useState(false);
  const [players, setPlayers] = useState();
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [debouncedValue, setDebouncedValue] = useState("");

  const location = useLocation();
  const url = links.filter((link) => link.url === location.pathname);

  var url_login = new URL(login_url_discord);
  var search_params = url_login.searchParams;
  search_params.append("redirect_uri", redirect_login_url);
  url_login.search = search_params.toString();
  const url_for_login_discord = url_login.toString();

  let activePageUrl = -1;
  if (url && url.length > 0) {
    activePageUrl = url[0].id;
  }

  const [activePage, setActivePage] = useState(activePageUrl);
  const navigate = useNavigate();

  const {
    openSidebar,
    isNavbarOpen,
    user,
    refreshUser,
    setUser,
    setAuthTokens,
  } = useNavContext();
  const [userInfo, setUserInfo] = useState(user);

  useEffect(() => {
    const fetchData = async () => {
      if (activePage === -1) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        const url = links.find((link) => link.url === location.pathname);
        if (url) setActivePage(url.id);
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    async function fetchData() {
      if (!userInfo) {
        var userDetails = localStorage.getItem("user");
        if (!userDetails) {
          await refreshUser();
        } else {
          setUser(userDetails);
          setUserInfo(JSON.parse(userDetails));
        }
      }
    }
    fetchData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (user) {
      let userDetails = user;
      if (typeof userDetails === "string") {
        userDetails = JSON.parse(userDetails);
      }
      setUserInfo(userDetails);
    }
  }, [user]);

  useEffect(() => {
    if (location.pathname.includes("/tournament/register-ok")) {
      return;
    }
    const params = new URLSearchParams(location.search);
    setCode(params.get("code"));
    if (!params.get("code")) {
      setError("");
      setLoading(false);
    }
  }, [location.search, location.pathname]);

  useEffect(() => {
    async function fetchData(code) {
      if (code && code.length > 0) {
        setLoading(true);
        setError(false);
        var url_login = api_v3_register(code, redirect_login_url);
        try {
          const response = await axios.post(url_login);
          if (response.status === 200) {
            setAuthTokens(
              response.data.access_token,
              response.data.access_token
            );
            setLoading(false);
          }
        } catch (error) {
          if (error.response.status > 500) {
            setError(
              `Se ha producido un error en el login (code=${4}). Por favor, inténtalo de nuevo.`
            );
          } else if (error.response.status === 409) {
            setError("");
            setLoading(false);
          } else {
            setError(
              `Se ha producido un inesperado (code=UNKNOW). Por favor, inténtalo de nuevo.`
            );
          }
          setLoading(false);
        }
      }
    }
    fetchData(code);
    // eslint-disable-next-line
  }, [code]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.keyCode === 27) {
        setIsSearchFocused(false);
        if (inputRef.current) inputRef.current.blur();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const fetchPlayers = async () => {
      setLoadPlayersInProgress(true);
      try {
        const response = await axios.get(api_v3_list_of_players());
        if (response.status === 200) {
          setPlayers(response.data);
          setLoadPlayersInProgress(false);
          return true;
        } else {
          setLoadPlayersInProgress(false);
          setLoadPlayersError(true);
          return false;
        }
      } catch (error) {
        setLoadPlayersInProgress(false);
        setLoadPlayersError(true);
      }
    };
    if (isSearchFocused && !players) {
      fetchPlayers();
    }
    setSearchValue("");
    if (isSearchFocused) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line
  }, [isSearchFocused]);

  useEffect(() => {
    if (searchValue.length < 2) {
      setDebouncedValue("");
      return;
    }
    const timeout = setTimeout(() => setDebouncedValue(searchValue), 250);
    return () => clearTimeout(timeout);
  }, [searchValue]);

  const fuse = useMemo(() => {
    if (!players) return null;
    return new Fuse(players, {
      keys: [
        { name: "username", weight: 0.7 },
        { name: "discordUsername", weight: 0.2 },
        { name: "playerId", weight: 0.1 },
      ],
      threshold: 0.6,
      includeScore: true,
    });
  }, [players]);

  const results = useMemo(() => {
    if (!fuse || debouncedValue.length < 2) return [];
    return fuse.search(debouncedValue).map((r) => r.item);
  }, [fuse, debouncedValue]);

  const displayPlayers = useMemo(() => results.slice(0, 20), [results]);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [results]);

  useEffect(() => {
    if (selectedIndex < 0 || !resultListRef.current) return;
    const items = resultListRef.current.children;
    if (items[selectedIndex]) {
      items[selectedIndex].scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  const handleSearchKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSelectedIndex((prev) =>
        prev < displayPlayers.length - 1 ? prev + 1 : prev
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (event.key === "Enter" && selectedIndex >= 0) {
      const player = displayPlayers[selectedIndex];
      if (player) {
        navigate(`/player/${player.playerId}`);
        setIsSearchFocused(false);
      }
    }
  };

  const handleSearchClick = () => {
    setIsSearchFocused(true);
  };

  return (
    <>
      <Bar style={{ display: `${isNavbarOpen ? "flex" : "none"}` }}>
        <Left>
          <Logo
            src="https://alexocampos.github.io/liga-espanhola-aoe2-static/assets/lea_t9.png"
            alt="Liga Española"
            onClick={openSidebar}
          />
        </Left>

        <SearchBar>
          <SearchIcon onClick={handleSearchClick} />
          <SearchInput
            ref={inputRef}
            type="text"
            placeholder="Buscar jugador..."
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            onFocus={() => setIsSearchFocused(true)}
          />
        </SearchBar>

        <Right>
          {loading ? (
            <MiniLoading />
          ) : null}
          {!userInfo && error ? (
            <ErrorIcon>
              <MdErrorOutline title={error} />
            </ErrorIcon>
          ) : null}
          {!userInfo && !loading ? (
            <LoginBtn onClick={() => window.open(url_for_login_discord, "_self")}>
              <DiscordLogo
                src="https://alexocampos.github.io/liga-espanhola-aoe2-static/assets/discord_2.png"
                alt="discord"
              />
              Iniciar sesión
            </LoginBtn>
          ) : null}
          {userInfo && !loading ? (
            <UserPill>
              <UserLink to="profile">
                <UserAvatar src={userInfo.discordAvatarURL} alt={userInfo.username} />
                <UserName>
                  {userInfo.username}
                  {userInfo.isAdmin ? " (admin)" : ""}
                  {!userInfo.isAdmin && userInfo.isModerator ? " (mod)" : ""}
                </UserName>
              </UserLink>
            </UserPill>
          ) : null}
          <SocialIcon
            src="https://alexocampos.github.io/liga-espanhola-aoe2-static/assets/twitter.jpg"
            alt="twitter"
            onClick={() => window.open(twitterUrl, "_blank")}
          />
          <SocialIcon
            src="https://alexocampos.github.io/liga-espanhola-aoe2-static/assets/discord_color.jpg"
            alt="discord"
            onClick={() => window.open(discordUrl, "_blank")}
          />
          <MenuBtn onClick={openSidebar}>
            <FiMenu />
          </MenuBtn>
        </Right>
      </Bar>
      {isSearchFocused && <SearchOverlay onClick={() => setIsSearchFocused(false)}>
        <SearchPanel onClick={(e) => e.stopPropagation()}>
          <SearchInputRow>
            <SearchInputLarge
              ref={inputRef}
              type="text"
              placeholder="Buscar jugador..."
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              autoFocus
            />
            <SearchHint>ESC para cerrar</SearchHint>
          </SearchInputRow>
          <SearchDivider />
          <SearchResults>
            {loadPlayersInProgress ? (
              <SearchLoading>
                <Loading />
              </SearchLoading>
            ) : null}
            {loadPlayersError ? (
              <SearchStatus>Error en la búsqueda. Recargue la página</SearchStatus>
            ) : null}
            {!loadPlayersInProgress && !loadPlayersError ? (
              <>
                {searchValue === "" && (
                  <SearchStatus>Empieza a escribir para buscar jugadores</SearchStatus>
                )}
                {searchValue.length === 1 && (
                  <SearchStatus>Escribe al menos 2 caracteres</SearchStatus>
                )}
                {searchValue.length >= 2 && results.length === 0 && debouncedValue.length >= 2 && (
                  <SearchStatus>No se encontraron jugadores</SearchStatus>
                )}
                {results.length > 0 ? (
                  <>
                    <ResultCount>
                      {displayPlayers.length} de {results.length} jugador
                      {results.length !== 1 ? "es" : ""} encontrado
                      {results.length !== 1 ? "s" : ""}
                    </ResultCount>
                    <ResultList ref={resultListRef}>
                      {displayPlayers.map((player, i) => (
                        <ResultRow
                          key={`player-${player.playerId}`}
                          to={`/player/${player.playerId}`}
                          onClick={() => setIsSearchFocused(false)}
                          style={{
                            animationDelay: `${i * 30}ms`,
                            ...(i === selectedIndex
                              ? {
                                  background: "rgba(60, 255, 208, 0.08)",
                                  borderColor: "rgba(60, 255, 208, 0.3)",
                                }
                              : {}),
                          }}
                        >
                          <ResultAvatar>
                            <ImageWithFallback
                              src={player.discordAvatarURL || randomAvatar()}
                              alt={player.username}
                              fallbackSrc={randomAvatar()}
                            />
                          </ResultAvatar>
                          <ResultInfo>
                            <ResultName>{player.username}</ResultName>
                            <ResultDiscord>
                              <DiscordMini src="https://alexocampos.github.io/liga-espanhola-aoe2-static/assets/discord.png" alt="" />
                              {(player.discordUsername || "").replace("#0", "")}
                            </ResultDiscord>
                          </ResultInfo>
                        </ResultRow>
                      ))}
                    </ResultList>
                  </>
                ) : null}
              </>
            ) : null}
          </SearchResults>
        </SearchPanel>
      </SearchOverlay>}
    </>
  );
};

const Bar = styled.header`
  padding: 0.5rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  position: sticky;
  top: 0;
  z-index: 99;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  background: rgba(19, 19, 19, 0.85);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    gap: 0.5rem;
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const Logo = styled.img`
  width: 48px;
  cursor: pointer;
  transition: opacity var(--v-transition);

  &:hover {
    opacity: 0.8;
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  max-width: 360px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  padding: 0.4rem 0.85rem;
  transition: border-color var(--v-transition);

  &:focus-within {
    border-color: var(--v-mint);
  }

  @media (max-width: 768px) {
    max-width: none;
  }
`;

const SearchIcon = styled(TfiSearch)`
  font-size: 14px;
  color: var(--v-fg-muted);
  cursor: pointer;
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  background: transparent;
  border: none;
  color: var(--v-fg);
  font-family: var(--v-font-body);
  font-size: 13px;
  width: 100%;
  outline: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.45);
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const MiniLoading = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-top-color: var(--v-mint);
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorIcon = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  color: #ff3b3b;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoginBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: var(--v-fg);
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.4rem 0.85rem;
  border-radius: 24px;
  cursor: pointer;
  transition: border-color var(--v-transition), background var(--v-transition);

  &:hover {
    border-color: var(--v-mint);
    color: var(--v-mint);
    background: rgba(60, 255, 208, 0.04);
  }

  @media (max-width: 768px) {
    span {
      display: none;
    }
  }
`;

const DiscordLogo = styled.img`
  width: 1rem;
  height: 1rem;
`;

const UserPill = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  padding: 0.2rem 0.65rem 0.2rem 0.2rem;
  transition: border-color var(--v-transition);

  &:hover {
    border-color: var(--v-mint);
  }
`;

const UserLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  text-decoration: none;
`;

const UserAvatar = styled.img`
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 50%;
  object-fit: cover;
`;

const UserName = styled.span`
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  color: var(--v-fg);

  @media (max-width: 768px) {
    display: none;
  }
`;

const SocialIcon = styled.img`
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity var(--v-transition);

  &:hover {
    opacity: 1;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const MenuBtn = styled.button`
  display: none;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  color: var(--v-fg);
  cursor: pointer;
  padding: 0.3rem 0.5rem;
  font-size: 1.3rem;
  line-height: 1;

  @media (max-width: 992px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

/* Search overlay — full-viewport spotlight */
const SearchOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #000;
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 12vh;
`;

const SearchPanel = styled.div`
  width: 100%;
  max-width: 680px;
  padding: 0 1.5rem;
`;

const SearchInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(60, 255, 208, 0.15);
`;

const SearchInputLarge = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-family: var(--v-font-display);
  font-size: clamp(36px, 6vw, 64px);
  font-weight: 400;
  color: var(--v-fg);
  caret-color: var(--v-mint);
  line-height: 1.1;
  text-transform: uppercase;
  letter-spacing: 1px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
    text-transform: uppercase;
  }
`;

const SearchHint = styled.span`
  font-family: var(--v-font-mono);
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.4);
  flex-shrink: 0;
`;

const SearchDivider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.03);
  margin: 0.5rem 0;
`;

const SearchResults = styled.div`
  margin-top: 1rem;
  max-height: 60vh;
  overflow-y: auto;
`;

const SearchLoading = styled.div`
  display: flex;
  justify-content: center;
  padding: 3rem 0;
`;

const SearchStatus = styled.p`
  font-family: var(--v-font-body);
  font-size: 14px;
  color: rgba(255, 255, 255, 0.65);
  margin: 2rem 0;
  text-align: center;
`;

const ResultCount = styled.p`
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--v-mint);
  margin: 0 0 1rem;
`;

const ResultList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 4px;
`;

const ResultRow = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.55rem 0.7rem;
  border-radius: 12px;
  text-decoration: none;
  border: 1px solid rgba(255, 255, 255, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease;
  animation: resultFadeIn 0.35s ease backwards;

  @keyframes resultFadeIn {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &:hover {
    border-color: rgba(60, 255, 208, 0.15);
    background: rgba(60, 255, 208, 0.03);
  }
`;

const ResultAvatar = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ResultInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 1px;
`;

const ResultName = styled.span`
  font-family: var(--v-font-body);
  font-size: 13px;
  font-weight: 600;
  color: var(--v-fg);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ResultDiscord = styled.span`
  font-family: var(--v-font-mono);
  font-size: 10px;
  color: var(--v-fg-muted);
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const DiscordMini = styled.img`
  width: 0.85rem;
  height: 0.85rem;
  object-fit: contain;
`;

export default Nav;
